import React from "react";
import type { Input, Output } from "./call";
import nakedCall from "./call";
import useQuery, { useGetQueryData, useMutate, useMutateBody } from "./useQuery";
import { useChannelUpdates, mergeItems } from "./ws/useChannelUpdates";
import { useGet } from "./useCall";

export type Activity = Output<"GET", "/activities">["body"][0];
export type ActivityType = Activity["type"];
// TODO: FIX FRONT IS DEPENDENT ON API TYPES

import type { ActivityIndexJSON } from "../../../service-diagnostics-core/src/api/activity/read";
import { comparatorBy } from "diagnostics-lib/comparator";
export type { ActivityIndexJSON };
export type CategoryActivityType = ActivityIndexJSON["CATEGORY_CREATED"]["properties"]["type"];
export type CategoryProjectActivityType =
  ActivityIndexJSON["PROJECT_CATEGORY_CREATED"]["properties"]["type"];

export type ActivityEventId =
  | {
      type: "FAULT_EVENT";
      id: {
        faultEventProjectId: string;
        faultEventFailureName: string;
        faultEventStartsAt: Date;
        faultEventId: string;
      };
    }
  | {
      type: "MANUAL_FAULT_EVENT";
      id: {
        manualFaultEventId: string;
      };
    };

const PATH = "/activities" as const;
const comparator = comparatorBy("updatedAt"); // TODO reverse order
const emptyArray: never[] = [];
export const useActivities = (
  projectIds: string[],
  types: ActivityType[],
  authorIds: string[],
  eventIds: ActivityEventId[],
  hideSystemUserActions: boolean,
) => {
  const params = React.useMemo(
    () =>
      ({
        projectIds,
        types,
        authorIds,
        eventIds,
        hideSystemUserActions,
      }) as Input<"GET", "/activities">["body"],
    [projectIds, types, authorIds, eventIds, hideSystemUserActions],
  );
  const key = React.useMemo(() => JSON.stringify(params), [params]);

  const {
    body: rawActivities,
    headers: { has_more: hasMore },
  } = useQuery(PATH, { body: params });

  const mutateItems = useMutateBody(PATH, { body: params });
  const getCachedItems = useGetQueryData(PATH, { body: params });
  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const { body: items } = await nakedCall("GET", PATH, { query: { since }, body: params });
      return [items, [] as string[]] as const;
    },
    [params],
  );
  const readItems = React.useCallback(() => getCachedItems()?.body, [getCachedItems]);
  const channelParams = React.useMemo(
    () =>
      ({
        key: "activities",
        projectIds,
        types,
        authorIds,
        eventIds,
      }) as const,
    [projectIds, types, authorIds, eventIds],
  );
  useChannelUpdates(channelParams, { readItems, fetchUpdatedItems, mutateItems });

  const [unreadFrom, setUnreadFrom] = React.useState(() => Date.now());
  React.useEffect(() => {
    setUnreadFrom(Date.now());
  }, [key]);

  const showUnread = React.useCallback(() => {
    setUnreadFrom(Date.now());
  }, []);
  const unreadCount = React.useMemo(
    () => rawActivities.filter(a => new Date(a.createdAt).getTime() > unreadFrom).length,
    [unreadFrom, rawActivities],
  );

  const earliestUpdatedAt = React.useMemo(
    () => Math.min(...rawActivities.map(activity => new Date(activity.updatedAt).getTime())) ?? 0,
    [rawActivities],
  );
  const mutate = useMutate(PATH, { query: {}, body: params });
  const call = useGet(PATH);
  const loading = call.loading;
  const fetchMore = React.useCallback(async () => {
    const { body: newItems, headers } = await call({
      query: { until: new Date(earliestUpdatedAt) },
      body: params,
    });

    mutate(old => ({
      ...old,
      body: mergeItems(old.body, newItems, emptyArray),
      headers: { ...old.headers, has_more: headers.has_more },
    }));
  }, [call, earliestUpdatedAt, mutate, params]);

  const activities = React.useMemo(
    () =>
      rawActivities
        .filter(a => new Date(a.createdAt).getTime() <= unreadFrom)
        .sort(comparator)
        .reverse(), // TODO comparator reverse order
    [unreadFrom, rawActivities],
  );

  return { activities, showUnread, unreadCount, fetchMore, hasMore, loading };
};
